import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const UploadHistory = ({ className = '' }) => {
  const [dateFilter, setDateFilter] = useState('7days');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock upload history data
  const uploadHistory = [
    {
      id: 'batch_001',
      timestamp: new Date('2024-10-14T10:30:00'),
      fileCount: 12,
      successCount: 11,
      failureCount: 1,
      totalSize: 45.2,
      processingTime: 28,
      avgConfidence: 0.89,
      status: 'completed',
      batchName: 'Morning Invoices - Oct 14'
    },
    {
      id: 'batch_002', 
      timestamp: new Date('2024-10-13T15:45:00'),
      fileCount: 8,
      successCount: 8,
      failureCount: 0,
      totalSize: 32.1,
      processingTime: 19,
      avgConfidence: 0.94,
      status: 'completed',
      batchName: 'Supplier Batch #2'
    },
    {
      id: 'batch_003',
      timestamp: new Date('2024-10-13T09:15:00'),
      fileCount: 25,
      successCount: 22,
      failureCount: 3,
      totalSize: 78.9,
      processingTime: 67,
      avgConfidence: 0.76,
      status: 'completed',
      batchName: 'Weekly Upload Batch'
    },
    {
      id: 'batch_004',
      timestamp: new Date('2024-10-12T14:20:00'),
      fileCount: 5,
      successCount: 3,
      failureCount: 2,
      totalSize: 18.7,
      processingTime: 15,
      avgConfidence: 0.65,
      status: 'needs_review',
      batchName: 'Problematic Invoices'
    },
    {
      id: 'batch_005',
      timestamp: new Date('2024-10-11T11:30:00'),
      fileCount: 15,
      successCount: 15,
      failureCount: 0,
      totalSize: 56.3,
      processingTime: 42,
      avgConfidence: 0.91,
      status: 'completed',
      batchName: 'Client Invoices - Oct 11'
    }
  ];

  const dateFilterOptions = [
    { value: '1day', label: 'Last 24 hours' },
    { value: '7days', label: 'Last 7 days' },
    { value: '30days', label: 'Last 30 days' },
    { value: '90days', label: 'Last 90 days' }
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'completed', label: 'Completed' },
    { value: 'needs-review', label: 'Needs Review' },
    { value: 'error', label: 'Failed' }
  ];

  const formatFileSize = (mb) => {
    return `${mb?.toFixed(1)} MB`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const getSuccessRate = (batch) => {
    return Math.round((batch?.successCount / batch?.fileCount) * 100);
  };

  const filteredHistory = uploadHistory?.filter(batch => {
    if (statusFilter !== 'all' && batch?.status !== statusFilter) return false;
    
    const daysDiff = Math.floor((new Date() - batch?.timestamp) / (1000 * 60 * 60 * 24));
    switch (dateFilter) {
      case '1day': return daysDiff <= 1;
      case '7days': return daysDiff <= 7;
      case '30days': return daysDiff <= 30;
      case '90days': return daysDiff <= 90;
      default: return true;
    }
  });

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="History" size={18} className="text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Upload History</h3>
        </div>

        <div className="flex items-center space-x-2">
          <Select
            options={dateFilterOptions}
            value={dateFilter}
            onChange={setDateFilter}
            className="w-36"
          />
          <Select
            options={statusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-32"
          />
        </div>
      </div>
      {/* History List */}
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {filteredHistory?.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Icon name="History" size={24} className="text-muted-foreground" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">No upload history</h4>
                <p className="text-xs text-muted-foreground">
                  Your recent uploads will appear here
                </p>
              </div>
            </div>
          </div>
        ) : (
          filteredHistory?.map((batch) => (
            <div key={batch?.id} className="p-4 hover:bg-muted/30 transition-smooth">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Package" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground">
                      {batch?.batchName}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(batch?.timestamp)}
                    </p>
                  </div>
                </div>

                <StatusIndicator 
                  status={batch?.status}
                  confidence={batch?.avgConfidence}
                  size="sm"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground">Files:</span>
                  <span className="ml-1 font-medium text-foreground">
                    {batch?.fileCount}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Success Rate:</span>
                  <span className={`ml-1 font-medium ${
                    getSuccessRate(batch) >= 90 ? 'text-success' : 
                    getSuccessRate(batch) >= 70 ? 'text-warning' : 'text-error'
                  }`}>
                    {getSuccessRate(batch)}%
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Size:</span>
                  <span className="ml-1 font-medium text-foreground">
                    {formatFileSize(batch?.totalSize)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Time:</span>
                  <span className="ml-1 font-medium text-foreground">
                    {formatDuration(batch?.processingTime)}
                  </span>
                </div>
              </div>

              {batch?.failureCount > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-error">
                    <Icon name="AlertCircle" size={12} className="inline mr-1" />
                    {batch?.failureCount} file{batch?.failureCount !== 1 ? 's' : ''} failed
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Retry Failed
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Summary Stats */}
      {filteredHistory?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-foreground">
                {filteredHistory?.length}
              </div>
              <div className="text-xs text-muted-foreground">Batches</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">
                {filteredHistory?.reduce((sum, batch) => sum + batch?.fileCount, 0)}
              </div>
              <div className="text-xs text-muted-foreground">Total Files</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-success">
                {Math.round(
                  filteredHistory?.reduce((sum, batch) => sum + (batch?.successCount / batch?.fileCount), 0) / 
                  filteredHistory?.length * 100
                )}%
              </div>
              <div className="text-xs text-muted-foreground">Avg Success</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-primary">
                {Math.round(
                  filteredHistory?.reduce((sum, batch) => sum + batch?.avgConfidence, 0) / 
                  filteredHistory?.length * 100
                )}%
              </div>
              <div className="text-xs text-muted-foreground">Avg Confidence</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadHistory;
